/**
 * Position state in millimetres
 *
 * The editor works in canvas pixels of the active template (R18); a design
 * position is stored in millimetres (R17, R34). This module is the one
 * crossing between the two: on save the area-relative canvas JSON is
 * divided by the template's Pixel Scale and the placement is the bounding
 * box of that JSON (R35); on load a placement changed without the JSON is
 * applied first, then the JSON is multiplied by the Pixel Scale of the
 * template the editor shows now (AE8, AE19).
 *
 * Pure functions only; the stores call them.
 */

import {
  alignCanvasJsonToPlacement,
  canvasJsonFromMm,
  canvasJsonToMm,
  isPlacementMm,
  placementMmFromCanvasJson,
  stripPixelContext,
  type CanvasObjectJSON,
  type PlacementAreaMm,
  type PlacementMm,
} from '@designdeck/shared';
import type { PrintContext } from '@/types/printContext';

/** A canvas JSON as the editor stores it: objects plus Fabric's extras. */
export interface PositionJson {
  objects?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

/** The print area a context describes, as the placement rules see it. */
export function placementAreaOfContext(
  ctx: Pick<
    PrintContext,
    | 'widthMm'
    | 'heightMm'
    | 'sizeTier'
    | 'sizeTierOverride'
    | 'productId'
    | 'productClass'
    | 'carryOverPercent'
    | 'carryOverAnchor'
    | 'carryOverTopOffsetMm'
  >
): PlacementAreaMm {
  return {
    widthMm: ctx.widthMm,
    heightMm: ctx.heightMm,
    sizeTier: ctx.sizeTier ?? null,
    sizeTierOverride: ctx.sizeTierOverride ?? null,
    productId: ctx.productId ?? null,
    productClass: ctx.productClass ?? null,
    carryOverPercent: ctx.carryOverPercent ?? null,
    carryOverAnchor: ctx.carryOverAnchor ?? null,
    carryOverTopOffsetMm: ctx.carryOverTopOffsetMm ?? null,
  };
}

function objectsOf(json: PositionJson): CanvasObjectJSON[] {
  return (json.objects ?? []) as CanvasObjectJSON[];
}

/** What a position row stores, computed from the editor's JSON. */
export interface PositionMmPayload {
  /** Canvas JSON in millimetre space on the position's area (R34). */
  canvasData: PositionJson;
  /** Bounding box of the objects; null for an emptied position. */
  placementMm: PlacementMm | null;
}

/**
 * The stored form of an area-relative canvas JSON written on `ctx`: pixel
 * context removed, divided by the context's Pixel Scale, placement from the
 * JSON's bounding box (R35), never from a live canvas.
 */
export function positionToMm(
  json: PositionJson,
  ctx: Pick<PrintContext, 'pxPerMm' | 'widthMm' | 'heightMm'>
): PositionMmPayload {
  const clean: PositionJson = {
    ...json,
    objects: objectsOf(json).map(
      (obj) => stripPixelContext(obj) as Record<string, unknown>
    ),
  };
  const mm = canvasJsonToMm(
    { ...clean, objects: objectsOf(clean) },
    ctx.pxPerMm
  ) as PositionJson;
  const placementMm = placementMmFromCanvasJson(
    { objects: objectsOf(mm) },
    { widthMm: ctx.widthMm, heightMm: ctx.heightMm }
  );
  return { canvasData: mm, placementMm };
}

/** Why a stored position could not be opened in the editor. */
export type PositionSkipReason =
  | 'no_placement'
  | 'no_objects'
  | 'ratio_mismatch';

export type PositionFromMm =
  | { kind: 'loaded'; json: PositionJson; aligned: boolean }
  | { kind: 'skipped'; reason: PositionSkipReason };

/**
 * The editor JSON of a stored position on the template with `pxPerMm`:
 * a position without a usable placement is skipped (no guessing, R5); a
 * placement that moved without its JSON (an agent's `set_placement`)
 * transforms the objects uniformly first (R35).
 */
export function positionFromMm(
  canvasData: PositionJson | null | undefined,
  placementMm: unknown,
  area: Pick<PlacementAreaMm, 'widthMm' | 'heightMm'>,
  pxPerMm: number
): PositionFromMm {
  if (!isPlacementMm(placementMm)) {
    return { kind: 'skipped', reason: 'no_placement' };
  }
  if (!canvasData || objectsOf(canvasData).length === 0) {
    return { kind: 'skipped', reason: 'no_objects' };
  }
  const aligned = alignCanvasJsonToPlacement(
    { objects: objectsOf(canvasData) },
    area,
    placementMm
  );
  if (aligned.kind === 'rejected') {
    return { kind: 'skipped', reason: aligned.reason };
  }
  const objects =
    aligned.kind === 'transformed' ? aligned.objects : objectsOf(canvasData);
  const json = canvasJsonFromMm(
    { ...canvasData, objects },
    pxPerMm
  ) as PositionJson;
  return { kind: 'loaded', json, aligned: aligned.kind === 'transformed' };
}
